/**
 * Unified dropdown hook — replaces `useSessionDropdowns`, `useDropdownBundle`,
 * and `useLoanDropdowns`.
 *
 * Strategy:
 *   1. Read the session-init cache (populated at login).
 *   2. If unavailable, fall back to the role-aware `getLoanInfoDropdownBundle`
 *      RTK query.
 *   3. Return a flat `{ data, isLoading }` object.
 *
 * Usage:
 *   const { data, isLoading } = useDropdowns();
 *   // data.loan_purpose, data.marital_status, data.employment_type, …
 */
import { useMemo } from 'react';
import { useGetSessionInitQuery } from 'redux_store/services/sessionInit';
import { useGetLoanInfoDropdownBundleQuery } from 'redux_store/services/client/profile';
import { useSelector } from 'react-redux';
import { userRoleData } from 'redux_store/slices/userRole';

export function useDropdowns() {
    const role = useSelector(userRoleData);
    const isAuthenticated = Boolean(role?.userRole);

    // Primary source: session-init cache
    const {
        data: sessionResp,
        isLoading: sessionLoading,
    } = useGetSessionInitQuery(undefined, {
        skip: !isAuthenticated,
    });

    const sessionDropdowns = sessionResp?.data?.dropdowns;

    // Fallback: bundle endpoint (role-aware, routes to /b2b/ or /b2c/)
    const {
        data: bundleResp,
        isLoading: bundleLoading,
    } = useGetLoanInfoDropdownBundleQuery(undefined, {
        skip: !isAuthenticated || !!sessionDropdowns,
    });

    const bundleDropdowns = bundleResp?.data;

    const data = useMemo(() => {
        const d = sessionDropdowns ?? bundleDropdowns ?? {};
        return {
            loan_purpose: d.loan_purpose,
            loan_type: d.loan_type,
            refinance_purpose: d.refinance_purpose,
            occupancy_type: d.occupancy_type,
            property_type: d.property_type,
            loan_program: d.loan_program,
            loan_term: d.loan_term,
            manner_held: d.manner_held,
            property_right: d.property_right,
            states: d.states,
            ownership: d.ownership,
            citizenship_status: d.citizenship_status,
            marital_status: d.marital_status,
            employment_type: d.employment_type,
            income_type: d.income_type,
            asset_type: d.asset_type,
            liability_type: d.liability_type,
            ethnicity: d.ethnicity,
            race: d.race,
            // Pass through any additional keys from the bundle
            ...d,
        };
    }, [sessionDropdowns, bundleDropdowns]);

    const isLoading = sessionLoading || (!sessionDropdowns && bundleLoading);

    return { data, isLoading };
}

export default useDropdowns;
